import type { ViewFilters } from "../../domain/app-types.ts";
import { defaultSavedViewFilters } from "./saved-view-state.ts";

export interface ActiveFilter { key: string; label: string }
export interface FilterNames {
  assignee(id: string): string;
  collection(id: string): string;
  epic?(id: string): string;
  cycle(id: string): string;
}

/** Describe predicates, not display preferences. Never expose identifiers as fallback labels. */
export function activeFilters(filters: ViewFilters, names: FilterNames): ActiveFilter[] {
  const result: ActiveFilter[] = [];
  const add = (key: keyof ViewFilters, label: string) => result.push({ key, label });
  if (filters.spaceScope === "all" || filters.spaceScope === "selected") add("spaceScope", filters.spaceScope === "all" ? "All accessible spaces" : `Selected spaces (${filters.spaceIds?.length || 0})`);
  if (filters.collectionIds?.length) add("collectionIds", `Selected collections (${filters.collectionIds.length})`);
  if (filters.customFields?.length) add("customFields", `Field filters (${filters.customFields.length})`);
  if (filters.statuses.length === 1) add("statuses", filters.statuses[0] === "open" ? "Open tasks" : "Completed tasks");
  if (filters.assigneeId) add("assigneeId", filters.assigneeId === "unassigned" ? "Unassigned" : `Assigned to ${names.assignee(filters.assigneeId)}`);
  if (filters.priorities.length) add("priorities", `Priority: ${filters.priorities.join(", ")}`);
  if (filters.tags.length) add("tags", `${filters.tagMatch === "any" ? "Any tag" : "All tags"}: ${filters.tags.join(", ")}`);
  if (filters.excludedTags?.length) add("excludedTags", `Exclude tags: ${filters.excludedTags.join(", ")}`);
  if (filters.categoryId) add("categoryId", filters.categoryId === "none" ? "No collection" : `Collection: ${names.collection(filters.categoryId)}`);
  if (filters.due !== "any") add("due", ({ today: "Due today", overdue: "Overdue", none: "No due date", next7: "Due in next 7 days", next30: "Due in next 30 days" })[filters.due]);
  if (filters.completedWithin === "week") add("completedWithin", "Completed in last 7 days");
  if (filters.sharing !== "any") add("sharing", filters.sharing === "shared" ? "Shared tasks" : "Not shared");
  if (filters.blocked) add("blocked", "Blocked or needs verification");
  if (filters.ready) add("ready", "Ready to start");
  if (filters.changedWithin === "week") add("changedWithin", "Changed in last 7 days");
  if (filters.epicId) add("epicId", filters.epicId === "none" ? "No goal" : `Goal: ${names.epic?.(filters.epicId) || "Selected goal"}`);
  if (filters.cycleId) add("cycleId", `Cycle: ${names.cycle(filters.cycleId)}`);
  if (filters.inactiveDays) add("inactiveDays", `Inactive for ${filters.inactiveDays}+ days`);
  if (filters.estimatedOnly) add("estimatedOnly", "Has time estimate");
  if (filters.taskIds?.length) add("taskIds", `Selected tasks (${filters.taskIds.length})`);
  return result;
}

/** Removing a status predicate means all statuses, not the default open-only view. */
export function withoutFilter(filters: ViewFilters, key: string): ViewFilters {
  if (key === "spaceScope") return { ...filters, spaceScope: "current", spaceIds: [], collectionIds: [] };
  if (key === "collectionIds" || key === "customFields") return { ...filters, [key]: [] };
  const defaults = { ...defaultSavedViewFilters(), statuses: [] };
  if (!Object.hasOwn(defaults, key) && key !== "ready") return filters;
  return { ...filters, [key]: key === "ready" ? false : defaults[key as keyof typeof defaults] };
}

export function unrestrictedFilters(): ViewFilters {
  return { ...defaultSavedViewFilters(), statuses: [] };
}
